import { de } from 'date-fns/locale/de';
import { registerLocale } from 'react-datepicker';

export const customLocale: any = {
  ...de,
  options: {
    ...de.options,
    weekStartsOn: 1,
  },
};

registerLocale('custom-de', customLocale);

export function getCalendarWeek(date: Date): number {
  const d: any = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart: any = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return weekNo;
}

export function formatHeader(dateStr: string): string {
  const dt = new Date(dateStr + 'T12:00:00');
  const weekdayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const dayOfWeek = dt.getDay();
  const weekdayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const weekdayShort = weekdayNames[weekdayIndex];
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  return `${weekdayShort}, ${dd}.${mm}.`;
}

export function getWeatherIcon(
  key: string,
  precipProbability?: number
): string {
  if (key === 'snow') return '\u{1F328}\uFE0F';
  if (key === 'thunderstorm') return '\u26C8\uFE0F';

  const precip = precipProbability ?? 0;
  if (precip >= 50) return '\u{1F327}\uFE0F';
  if (precip >= 20) return '\u{1F326}\uFE0F';

  const baseIcons: Record<string, string> = {
    clear: '\u2600\uFE0F',
    partly_cloudy: '\u26C5',
    fog: '\u26C5',
  };
  return baseIcons[key] || '\u26C5';
}

export function addOneHour(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const newH = (h + 1) % 24;
  return `${String(newH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function hexToRgb(hex: string): {
  r: number;
  g: number;
  b: number;
} {
  const cleanHex = hex.replace('#', '');
  return {
    r: parseInt(cleanHex.substring(0, 2), 16),
    g: parseInt(cleanHex.substring(2, 4), 16),
    b: parseInt(cleanHex.substring(4, 6), 16),
  };
}

export function getWAColor(waColor: string): string {
  return waColor || '#5271A5';
}

export function getAbsenceTypeLabel(type: string): string {
  const types: Record<string, string> = {
    HOLIDAY: 'Holiday',
    SICK: 'Sick',
    SICK_CHILD: 'Child sick',
    OVERTIME: 'Comp time',
    EXAM: 'Exam',
    WISH_OFF: 'Off',
  };
  return types[type?.toUpperCase()] || type || 'Off';
}

export const CALENDAR_WIDTH = 320;

export const DEFAULT_SHIFT_DATA = {
  start_time_date: '',
  start_time_time: '09:00',
  end_time_time: '17:00',
  employees_needed: 1,
  work_area: '',
  notes: '',
  weekdays: [] as number[],
  interval: 1,
  repeat_until: '',
  never_ends: false,
  crm_task: null as number | null,
  crm_task_title: '' as string,
};

export const DEFAULT_EDIT_SHIFT_DATA = {
  shiftId: null as number | null,
  startDate: '',
  startTime: '',
  endTime: '',
  employeesNeeded: 1,
  workArea: '',
  notes: '',
  scope: 'this',
  crm_task: null as number | null,
  crm_task_title: '' as string,
};

import ShiftService from '../services/ShiftService';
import { HttpError } from '../../../core/services/HttpService';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import shiftErrorHandler from '../services/ShiftErrorHandler';
import dateUtil from '../../../utils/dateUtil';
import { toast } from 'react-toastify';

export async function createAssignmentWithOverlapCheck(
  payload: any,
  autoRemove: boolean,
  fetchTableShifts: () => Promise<any>
): Promise<void> {
  const finalPayload = { ...payload };
  if (autoRemove) finalPayload.auto_remove_overlap = true;
  try {
    await ShiftService.createShiftAssignment(finalPayload);
    await fetchTableShifts();
  } catch (error: any) {
    if (error instanceof HttpError && error.status === 409) {
      const msg = error.data?.detail || 'Overlap found. Remove older SHIFT?';
      if (window.confirm(msg)) {
        await createAssignmentWithOverlapCheck(payload, true, fetchTableShifts);
      }
    } else {
      console.error('Assignment error =>', error);
      if (error instanceof HttpError) {
        if (!shiftErrorHandler.handle(error)) globalErrorHandler.handle(error);
      }
    }
  }
}

export async function toggleAssignment(
  shiftId: number,
  empId: number,
  isAutoAssign: boolean,
  tableShifts: any[],
  allEmployees: any[],
  fetchTableShifts: () => Promise<any>
): Promise<void> {
  let foundShift: any = null;
  outer: for (const dayObj of tableShifts) {
    if (!dayObj.shifts) continue;
    for (const s of dayObj.shifts) {
      if (s.id === shiftId) { foundShift = s; break outer; }
    }
  }
  if (!foundShift) return;

  const emp = allEmployees.find((e: any) => e.id === empId);
  if (emp && foundShift.work_area) {
    const isQualified = emp.work_areas.includes(foundShift.work_area);
    if (!isQualified && !isAutoAssign) {
      const waName = foundShift.work_area__name || 'this WorkArea';
      if (!window.confirm(`Employee ${emp.first_name} is NOT qualified for ${waName}.\nAssign anyway?`)) return;
    }
  }

  const assignedObj = foundShift.assigned_employees.find((a: any) => a.id === empId);
  if (assignedObj) {
    try {
      await ShiftService.deleteShiftAssignment(assignedObj.assignment_id);
      await fetchTableShifts();
    } catch (error) {
      console.error('Error remove assignment =>', error);
      if (error instanceof HttpError) {
        if (!shiftErrorHandler.handle(error)) globalErrorHandler.handle(error);
      }
    }
  } else if (isAutoAssign) {
    try {
      await ShiftService.createShiftAssignment({ shift: shiftId, employee: empId });
      await fetchTableShifts();
    } catch (error) {
      console.error('Error creating assignment =>', error);
      if (error instanceof HttpError) {
        if (!shiftErrorHandler.handle(error)) globalErrorHandler.handle(error);
      }
    }
  } else {
    await createAssignmentWithOverlapCheck({ shift: shiftId, employee: empId }, false, fetchTableShifts);
  }
}

export async function executeAutoAssignLogic(
  allEmployees: any[],
  tableShifts: any[],
  handleToggle: (shiftId: number, empId: number, isAuto: boolean) => Promise<void>,
  fetchShifts: () => Promise<any>,
  addLog: (msg: string) => void,
  setIsAssigning: (v: boolean) => void
): Promise<void> {
  addLog('Starting auto-assignment with extended constraints...');
  const employeesStats = allEmployees.map((e: any) => ({
    ...e,
    weekly_hours: e.contract_hours_per_week || e.weekly_hours || 40,
    balance_hours: e.balance_hours || 0,
    assignedShiftsCount: 0,
    assignedShiftsSumOfHours: 0,
    assignedDays: new Set(),
  }));

  const openShifts: any[] = [];
  tableShifts.forEach((dayObj: any) => {
    dayObj.shifts?.forEach((shift: any) => {
      const assignedCount = shift.assigned_employees?.length || 0;
      if (assignedCount < shift.employees_needed) {
        openShifts.push({
          shiftId: shift.id,
          workArea: shift.work_area,
          neededCount: shift.employees_needed - assignedCount,
          startTime: new Date(shift.start_time),
          endTime: new Date(shift.end_time),
        });
      }
    });
  });
  addLog(`Found ${openShifts.length} open shifts...`);
  openShifts.sort((a: any, b: any) => a.startTime - b.startTime);

  const canAssign = (empStats: any, shift: any) => {
    if (!empStats.work_areas.includes(shift.workArea)) return false;
    const shiftHours = (shift.endTime - shift.startTime) / 3600000;
    if (empStats.assignedShiftsSumOfHours + shiftHours > empStats.weekly_hours * 1.5) return false;
    return true;
  };

  let assignmentsMade = 0;
  try {
    for (let round = 1; round <= 5; round++) {
      let placedThisRound = 0;
      addLog(`\n=== Round ${round} \u2013 Distribution ===`);
      employeesStats.sort((A: any, B: any) => {
        if (A.assignedShiftsCount !== B.assignedShiftsCount) return A.assignedShiftsCount - B.assignedShiftsCount;
        if (A.balance_hours !== B.balance_hours) return A.balance_hours - B.balance_hours;
        return A.id - B.id;
      });
      for (const shift of openShifts) {
        if (shift.neededCount <= 0) continue;
        for (const empStats of employeesStats) {
          if (shift.neededCount <= 0) break;
          if (empStats.assignedShiftsCount >= round) continue;
          if (!canAssign(empStats, shift)) continue;
          addLog(`Trying Shift #${shift.shiftId} for Employee #${empStats.id}...`);
          try {
            await handleToggle(shift.shiftId, empStats.id, true);
          } catch (error: any) {
            addLog(`\u26A0 Failed: ${error.message}`);
            continue;
          }
          await fetchShifts();
          shift.neededCount--;
          empStats.assignedShiftsCount++;
          empStats.assignedShiftsSumOfHours += (shift.endTime - shift.startTime) / 3600000;
          assignmentsMade++;
          placedThisRound++;
          addLog(` \u2713 Shift #${shift.shiftId} \u2192 Employee #${empStats.id} (${shift.neededCount} still needed)`);
          await new Promise(res => setTimeout(res, 200));
        }
      }
      addLog(`Round ${round} \u2192 ${placedThisRound} assigned`);
    }
    addLog(`\nAuto-assignment complete. Total assigned: ${assignmentsMade}`);
  } catch (error: any) {
    console.error('AutoAssign =>', error);
    addLog(`\u274C Unexpected error: ${error.message}`);
  } finally {
    setIsAssigning(false);
  }
}

export async function handleEditShiftSaveFn(
  editShiftData: any,
  setShowEditShift: (v: boolean) => void,
  fetchTableShifts: () => Promise<any>
): Promise<void> {
  const { shiftId, startDate: sd, startTime, endTime, employeesNeeded, workArea, notes, scope, crm_task } = editShiftData;
  let startUtc = dateUtil.createTenantTimeAsUTC(sd, startTime);
  let endUtc = dateUtil.createTenantTimeAsUTC(sd, endTime);
  if (!startUtc || !endUtc) { toast.warning('Invalid date/time'); return; }
  if (endUtc <= startUtc) {
    const nextDay = new Date(sd);
    nextDay.setDate(nextDay.getDate() + 1);
    const nextDayStr = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
    endUtc = dateUtil.createTenantTimeAsUTC(nextDayStr, endTime) || endUtc;
  }
  try {
    await ShiftService.updateShift(shiftId, {
      start_time: startUtc, end_time: endUtc,
      employees_needed: employeesNeeded, work_area: workArea,
      notes: notes || '', crm_task: crm_task || null,
    }, scope);
    setShowEditShift(false);
    await fetchTableShifts();
  } catch (error) {
    console.error('Edit SHIFT =>', error);
    if (error instanceof HttpError) {
      if (!shiftErrorHandler.handle(error)) globalErrorHandler.handle(error);
    }
  }
}

export async function handleEditShiftDeleteFn(
  editShiftData: any,
  setShowEditShift: (v: boolean) => void,
  fetchTableShifts: () => Promise<any>
): Promise<{ deletedIds: number[]; fetchTableShifts: () => Promise<any> } | null> {
  const { shiftId, scope } = editShiftData;
  try {
    const result = await ShiftService.deleteShift(shiftId, scope);
    setShowEditShift(false);
    await fetchTableShifts();
    const deletedIds: number[] = result?.deleted_ids || [shiftId];
    return { deletedIds, fetchTableShifts };
  } catch (error) {
    console.error('Delete SHIFT =>', error);
    if (error instanceof HttpError) {
      if (!shiftErrorHandler.handle(error)) globalErrorHandler.handle(error);
    }
    return null;
  }
}

export async function handleClearAllConfirmFn(
  tableShifts: any[],
  fetchTableShifts: () => Promise<any>,
  setShowClearAllModal: (v: boolean) => void
): Promise<void> {
  try {
    const allAssignments: number[] = [];
    tableShifts.forEach((dayObj: any) => {
      dayObj.shifts?.forEach((shift: any) => {
        shift.assigned_employees?.forEach((emp: any) => {
          allAssignments.push(emp.assignment_id);
        });
      });
    });
    if (allAssignments.length === 0) {
      toast.info('No assignments to clear.');
      setShowClearAllModal(false);
      return;
    }
    for (const assignmentId of allAssignments) {
      await ShiftService.deleteShiftAssignment(assignmentId);
    }
    await fetchTableShifts();
    setShowClearAllModal(false);
  } catch (err: any) {
    console.error('Error clearing assignments =>', err);
    toast.error('Error clearing assignments: ' + err.message);
  }
}

export function moveShiftToCell(
  selected: any,
  destDate: string,
  destWaId: number,
  fetchTableShifts: () => Promise<any>
): Promise<void> {
  const sDT = dateUtil.convertUTCToTenantTime(selected.start_time) || new Date(selected.start_time);
  const eDT = dateUtil.convertUTCToTenantTime(selected.end_time) || new Date(selected.end_time);
  const pad = (n: number) => String(n).padStart(2, '0');
  const sTimeStr = `${pad(sDT.getHours())}:${pad(sDT.getMinutes())}`;
  const eTimeStr = `${pad(eDT.getHours())}:${pad(eDT.getMinutes())}`;
  let dndStartUtc = dateUtil.createTenantTimeAsUTC(destDate, sTimeStr);
  let dndEndUtc = dateUtil.createTenantTimeAsUTC(destDate, eTimeStr);
  if (dndStartUtc && dndEndUtc && dndEndUtc <= dndStartUtc) {
    const nextDay = new Date(destDate);
    nextDay.setDate(nextDay.getDate() + 1);
    const nextDayStr = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
    dndEndUtc = dateUtil.createTenantTimeAsUTC(nextDayStr, eTimeStr) || dndEndUtc;
  }
  return ShiftService.updateShift(
    selected.id,
    {
      start_time: dndStartUtc || new Date().toISOString(),
      end_time: dndEndUtc || new Date().toISOString(),
      work_area: destWaId,
      employees_needed: selected.employees_needed || 1,
      notes: selected.notes || '',
    },
    'this'
  ).then(() => fetchTableShifts()).then(() => {});
}
